<script setup lang="ts">
import { SparklesIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { computed, onMounted } from 'vue'

import type { DropdownOption } from '@/components/common/ui/inputs/inputTypes'
import type { ManagementSuggestionsContext } from '@/composables/organization/useManagementSuggestions'

import ManagementSuggestionLoading from '@/components/editRelationGroup/managementSuggestions/ManagementSuggestionLoading.vue'
import ManagementSuggestionRow from '@/components/editRelationGroup/managementSuggestions/ManagementSuggestionRow.vue'
import { useBackendConstantsStore } from '@/stores/backendConstantsStore'
import { getUrlHost } from '@/utilities/managementSuggestions'
import { translationFunctionBackend, translationFunctionDefault } from '@/utilities/translations'

interface ManagementSuggestionListProps {
  context: ManagementSuggestionsContext
  mode: 'create' | 'edit'
  // Replaces the plain acceptance, e.g. to save the person at once
  onAcceptSuggestion?: ((id: number) => Promise<void>) | undefined
  onClose?: (() => void) | undefined
}

const props = defineProps<ManagementSuggestionListProps>()

const tfBackend = translationFunctionBackend()
const tfDefault = translationFunctionDefault()

const backendConstantsStore = useBackendConstantsStore()
const { fetchRoleList, fetchSalutationList } = backendConstantsStore
const { roleOptionList, salutationOptionList } = storeToRefs(backendConstantsStore)

const roleOptions = computed((): DropdownOption<null | string>[] => [
  {
    isPlaceholderOption: true,
    optionLabel: tfBackend('salutations.noneOptionLabel'),
    optionValue: null,
  },
  ...roleOptionList.value,
])

const searchState = computed(() => props.context.searchState.value)
// The server names the company it searched; before its answer the request is the best information
const searchedName = computed(() => {
  return (
    props.context.searchHints.value?.organization_name ??
    props.context.searchRequest.value?.organization_name ??
    ''
  )
})
const searchedHost = computed(() => {
  return getUrlHost(
    props.context.searchHints.value?.website ?? props.context.searchRequest.value?.website,
  )
})

const accept = async (id: number): Promise<void> => {
  if (props.onAcceptSuggestion) {
    await props.onAcceptSuggestion(id)
  } else {
    props.context.accept(id)
  }
}

onMounted(async () => {
  await Promise.all([fetchRoleList(), fetchSalutationList()])
})
</script>

<template>
  <div
    v-if="searchState !== 'idle'"
    class="rounded-xl bg-[linear-gradient(135deg,#cae8ff,#007ddd)] p-px"
    :class="{ 'mx-8 mb-8': mode === 'create', 'mt-4': mode === 'edit' }"
  >
    <div class="flex flex-col gap-2 rounded-[11px] bg-lochmara-50 p-5">
      <div class="flex items-start gap-2 text-base font-medium leading-6 text-lochmara-600">
        <SparklesIcon aria-hidden="true" class="size-6 shrink-0 pt-px text-lochmara-500" />
        <span class="flex flex-1 flex-col">
          <span>{{ tfDefault('managementSuggestions.title') }}</span>
          <span v-if="searchedName" class="text-sm font-normal text-black-500">
            {{ tfDefault('managementSuggestions.searchedFor', { name: searchedName }) }}
            <template v-if="searchedHost">· {{ searchedHost }}</template>
          </span>
        </span>
        <button
          v-if="onClose"
          :aria-label="tfDefault('managementSuggestions.close')"
          class="text-lochmara-600 hover:text-lochmara-800"
          type="button"
          @click="onClose"
        >
          <XMarkIcon class="size-5" />
        </button>
      </div>
      <ManagementSuggestionLoading
        v-if="searchState === 'loading'"
        :organization-name="searchedName"
      />
      <p v-else-if="searchState === 'error'" class="text-sm text-radical_red-600">
        {{ tfDefault('managementSuggestions.searchError') }}
      </p>
      <p v-else-if="context.suggestions.value.length === 0" class="text-sm text-black-700">
        {{ tfDefault('managementSuggestions.noResults') }}
      </p>
      <ul v-else>
        <ManagementSuggestionRow
          v-for="suggestion in context.suggestions.value"
          :key="suggestion.id"
          :mode="mode"
          :role-options="roleOptions"
          :salutation-options="salutationOptionList"
          :saving="context.saving.value"
          :suggestion="suggestion"
          @accept="() => accept(suggestion.id)"
          @discard="() => context.discard(suggestion.id)"
          @reopen="() => context.reopen(suggestion.id)"
          @select-duplicate="(choice) => context.selectDuplicate(suggestion.id, choice)"
          @update="(values) => context.updateValues(suggestion.id, values)"
        />
      </ul>
    </div>
  </div>
</template>
